import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Banknote, Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import { blockNumberWheel } from "@/components/shared/line-grid";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney } from "@/features/vendors/constants";
import { useVendors } from "@/features/vendors/api";
import { PAYMENT_METHODS, labelFor } from "@/features/payments/constants";
import {
  useDeleteVendorPayment,
  useUpsertVendorPayment,
  useVendorPayments,
  vendorPaymentToForm,
} from "@/features/vendor-payments/api";
import {
  emptyVendorPaymentForm,
  vendorPaymentFormSchema,
  type VendorPaymentFormValues,
} from "@/features/vendor-payments/schema";
import type { VendorPaymentListRow } from "@/features/vendor-payments/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function VendorPaymentsPage({
  vendorId: vendorIdProp,
  embedded,
}: {
  vendorId?: string;
  embedded?: boolean;
} = {}) {
  return (
    <ErrorBoundary title="Vendor payments crashed">
      <VendorPaymentsInner vendorIdProp={vendorIdProp} embedded={embedded} />
    </ErrorBoundary>
  );
}

function VendorPaymentsInner({
  vendorIdProp,
  embedded,
}: {
  vendorIdProp?: string;
  embedded?: boolean;
}) {
  const vendorFilter = vendorIdProp;
  const { data = [], isLoading, error } = useVendorPayments({ vendorId: vendorFilter });
  const { data: vendors = [] } = useVendors();
  const upsert = useUpsertVendorPayment();
  const remove = useDeleteVendorPayment();

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<VendorPaymentListRow | null>(null);
  const [deleting, setDeleting] = useState<VendorPaymentListRow | null>(null);

  const form = useForm<VendorPaymentFormValues>({
    resolver: zodResolver(vendorPaymentFormSchema) as unknown as Resolver<VendorPaymentFormValues>,
    defaultValues: {
      ...emptyVendorPaymentForm,
      vendor_id: vendorFilter ?? "",
    },
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.reset(vendorPaymentToForm(editing));
    } else {
      form.reset({
        ...emptyVendorPaymentForm,
        vendor_id: vendorFilter ?? "",
      });
    }
  }, [open, editing, form, vendorFilter]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return data;
    return data.filter((row) =>
      [row.vendor_name, row.vendor_code, row.reference, row.po_number, row.notes]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [data, q]);

  const total = useMemo(() => filtered.reduce((s, r) => s + r.amount, 0), [filtered]);

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((v) => v.is_active)
        .map((v) => ({
          id: v.id,
          label: `${v.code} · ${v.name}`,
          keywords: v.name,
          meta: formatMoney(v.outstanding_balance),
        })),
    [vendors],
  );

  const startCreate = useCallback(() => {
    setEditing(null);
    setOpen(true);
  }, []);

  const errors = form.formState.errors;

  async function onSubmit(values: VendorPaymentFormValues) {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
    setEditing(null);
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Configure Supabase to record vendor payments."
      />
    );
  }

  return (
    <div className="space-y-3">
      {!embedded ? (
        <PageHeader
          title="Vendor payments"
          description="Pay vendors · reduce AP outstanding"
          actions={
            <Button type="button" size="sm" onClick={startCreate}>
              <Plus />
              Record payment
            </Button>
          }
        />
      ) : (
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={startCreate}>
            <Plus />
            Record payment
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-8 max-w-xs text-xs"
          placeholder="Search vendor, ref, PO…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="rounded border bg-card px-3 py-1.5 text-xs">
          Total <strong className="tabular-nums">{formatMoney(total)}</strong>
          <span className="text-muted-foreground"> · {filtered.length}</span>
        </div>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <EmptyState title="Failed to load" description={(error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No payments"
          description="Record a payment against vendor outstanding."
          action={
            <Button size="sm" onClick={startCreate}>
              <Plus />
              Record payment
            </Button>
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                {!vendorFilter ? <TableHead>Vendor</TableHead> : null}
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>PO</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} className="text-xs">
                  <TableCell>{format(parseISO(row.payment_date), "dd MMM yyyy")}</TableCell>
                  {!vendorFilter ? (
                    <TableCell>
                      <span className="font-mono text-muted-foreground">{row.vendor_code}</span>{" "}
                      {row.vendor_name}
                    </TableCell>
                  ) : null}
                  <TableCell>{labelFor(PAYMENT_METHODS, row.payment_method)}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {row.reference || "—"}
                  </TableCell>
                  <TableCell className="font-mono">{row.po_number || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(row.amount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setEditing(row);
                          setOpen(true);
                        }}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setDeleting(row)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Banknote className="size-4" />
              {editing ? "Edit payment" : "Record payment"}
            </SheetTitle>
          </SheetHeader>
          <form className="mt-4 space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGrid>
              <Field label="Vendor" required span={12} error={errors.vendor_id?.message}>
                <EntityPicker
                  value={form.watch("vendor_id") ?? ""}
                  disabled={Boolean(vendorFilter) && !editing}
                  placeholder="Vendor…"
                  recentKey="vendors"
                  options={vendorOptions}
                  onChange={(id) => {
                    form.setValue("vendor_id", id, { shouldDirty: true, shouldValidate: true });
                    form.setValue("purchase_order_id", "", { shouldDirty: true });
                  }}
                />
              </Field>
              <Field label="Date" required span={6} error={errors.payment_date?.message}>
                <Input type="date" {...form.register("payment_date")} />
              </Field>
              <Field label="Amount" required span={6} error={errors.amount?.message}>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  className="fs-no-spin"
                  onWheel={blockNumberWheel}
                  {...form.register("amount")}
                />
              </Field>
              <Field label="Method" span={6}>
                <NativeSelect {...form.register("payment_method")}>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Reference" span={6} hint="UTR / cheque no">
                <Input {...form.register("reference")} />
              </Field>
              <Field label="Notes" span={12}>
                <Textarea rows={2} className="text-xs" {...form.register("notes")} />
              </Field>
            </FieldGrid>
            <SheetFooter className="gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : "Save · post AP"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete payment?"
        description="AP payment will be reversed and outstanding recalculated."
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
}
